import React, { useState } from "react";

const C = { ink: "#111615", teal: "#35c4c7", aqua: "#d9f6f2", cream: "#f7f5ef", line: "#d9dfdc" };

const features = [
  ["AI-powered discovery", "Search transport, destinations and trusted local experiences in one natural conversation."],
  ["One connected journey", "Move from inspiration to planning and booking without switching between disconnected services."],
  ["Built for conversion", "Turn traveller intent into measurable bookings, qualified leads and meaningful engagement."],
  ["Enterprise ready", "Modular infrastructure, verified content and analytics designed for tourism organisations."],
];

const trustItems = [
  ["omnichannel", "Omnichannel travel experience", "One continuous journey across web, app, messaging, voice and kiosks"],
  ["unified", "Unified traveller context", "A living view of intent, itinerary, preferences, and service history"],
  ["orchestration", "Travel agent orchestration", "MCP and A2A-enabled specialists coordinating an accountable outcome"],
  ["governance", "AI governance & guardrails", "Controlled, observable, traceable, and human-supervised"],
];

const platform = [
  ["01", "AI search", "Conversational discovery across transport, places, stays and activities.", "search"],
  ["02", "Travel graph", "Structured, verified destination information that AI can understand.", "graph"],
  ["03", "Journey builder", "Personalised itineraries that respond to time, budget and preferences.", "route"],
  ["04", "Booking layer", "Deep links and partner integrations that turn plans into action.", "calendar"],
];

const capabilities = [
  ["search", "Conversational Journey Search", "Let travellers describe where they want to go, when and under what constraints—then surface suitable routes, services and fares.", "RAILWAYS · AIRLINES · TRANSPORT"],
  ["clock", "Live Schedules & Disruption Support", "Connect approved live schedules, delays, platform changes and service alerts to timely passenger guidance.", "RAILWAYS · AIRPORTS · TERMINALS"],
  ["location", "AI Wayfinding & Hub Assistance", "Guide visitors to gates, platforms, facilities, retails, and onward connections using location-aware assistance.", "AIRPORTS · STATIONS · ATTRACTIONS"],
  ["globe", "Official Destination Discovery", "Combine official destination intelligence with current creator recommendations and licensed local businesses.", "TOURISM BOARDS · LOCAL COUNCILS"],
  ["booking", "Personalised Booking Paths", "Turn traveller intent into relevant journeys, stays, experiences, upgrades and authorised booking hand-offs.", "EVERY PASSENGER & VISITOR CHANNEL"],
  ["chat", "Multilingual 24/7 Assistance", "Provide omnichannel natural-language service across web, mobile, messaging, voice, kiosks, and service desks.", "AIRLINES · OPERATORS · DESTINATIONS"],
  ["network", "Agentic Travel Commerce", "Coordinate inventory, offers, booking, payment and partner services through governed agents and approved systems.", "TRANSPORT · HOSPITALITY · EXPERIENCE"],
  ["service", "Post-Purchase Travel Service", "Support confirmations, changes, disruption recovery, refunds and human escalation after checkout.", "TRANSPORT · HOTELS · ATTRACTIONS"],
];

const steps = [
  ["01", "Connect your data", "Bring together transport, tourism content, inventory and operational knowledge."],
  ["02", "Structure and verify", "Epic converts fragmented information into an AI-ready travel knowledge graph."],
  ["03", "Deploy your AI", "Launch a branded assistant across web, app, QR and messaging channels."],
  ["04", "Measure outcomes", "Track questions, intent, gaps, referrals and conversion opportunities."],
];

const useCases = [
  {
    num: "01",
    label: "TRAVEL INFRASTRUCTURE & ENTERPRISES",
    title: "Turn every traveller interaction into an intelligent service and commercial opportunity.",
    desc: "For railways, airports, bus terminals, transport operators, telecommunications providers, integrated destinations and major attractions connecting AI with live services and booking infrastructure.",
    checks: ["Live schedules, fares and availability", "Ticketing & enterprise-system integration", "Omnichannel passenger & visitor assistance", "Ancillary, retail and destination revenue"],
    tags: ["RAILWAYS", "AIRPORTS", "TRANSPORT HUBS", "TELCOS", "MAJOR ATTRACTIONS"],
    btn: "Invite us to your RFP / Tender"
  },
  {
    num: "02",
    label: "TOURISM MINISTRIES, BOARDS & LOCAL GOVERNMENT",
    title: "Bring creator-led inspiration into an official, intelligent destination service.",
    desc: "Modern tourists consult Instagram and YouTube creators for current, firsthand inspiration - not static tourism pages alone. Platforms such as VisitMalaysia.ai aggregate those creator experiences into an official national tourism service, grounded by approved destination information.",
    checks: ["Instagram & YouTube creator aggregation", "Official, current destination intelligence", "AI itinerary and destination planning", "Licensed business recommendations"],
    tags: ["TOURISM MINISTRIES", "TOURISM BOARDS", "LOCAL COUNCILS"],
    btn: "Modernize your platform"
  },
  {
    num: "03",
    label: "TRAVEL CREATORS",
    title: "Turn travel influence into a bookable business.",
    desc: "Bring current, firsthand travel experiences from Instagram and YouTube into official destination AI, powering real searches and bookable itineraries - while retaining the audiences trust.",
    checks: ["Reach travellers through official tourism AI", "Launch an AI travel storefront", "Turn content into bookable itineraries", "Earn from hotels, transport & experiences"],
    tags: ["TRAVEL CREATORS", "PUBLISHERS"],
    btn: "Build your travel store"
  }
];

const faqs = [
  ["How does Epictravel.ai differ from general frontier AI models such as ChatGPT or Gemini?", "Frontier models are conversational generalists. Epictravel.ai is a specialist travel and transport intelligence layer that grounds those models with proprietary travel graphs, approved knowledge, enterprise guardrails and MCP gateways connected to live services and authorised transactions."],
  ["How does Epictravel.ai connect securely to existing transit and booking systems?", "Through secure API integrations, MCP gateways, and enterprise connectors that maintain data sovereignty while enabling real-time access to schedules, availability, and booking systems."],
  ["How is commercial value generated and measured?", "The platform tracks traveller intent, conversion pathways, booking completions, referral actions, and revenue attribution across all connected channels and partner services."],
  ["Can we begin with one deployment before expanding across the organisation?", "Yes. The modular architecture supports phased deployment, starting with a single use case or channel before expanding to additional services and touchpoints."],
  ["Why engage Epictravel.ai if we already have an IT or AI team?", "Epictravel.ai provides specialist travel domain expertise, pre-built integrations, and governed AI infrastructure that accelerates deployment while your team focuses on core business systems."],
];

function Icon({ name, size = 22 }) {
  const paths = {
    arrow: <><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></>,
    spark: <><path d="m12 3 1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3Z"/><path d="m5 16 .7 2.3L8 19l-2.3.7L5 22l-.7-2.3L2 19l2.3-.7L5 16Z"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></>,
    omnichannel: <><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></>,
    unified: <><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/></>,
    orchestration: <><path d="m12 3 1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3Z"/></>,
    governance: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></>,
    search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    graph: <><path d="m12 3 1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3Z"/><path d="m5 16 .7 2.3L8 19l-2.3.7L5 22l-.7-2.3L2 19l2.3-.7L5 16Z"/></>,
    route: <><path d="m12 3 1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3Z"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    location: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    booking: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M13 8h5"/><path d="M13 12h5"/><path d="M13 16h5"/></>,
    chat: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    network: <><circle cx="12" cy="5" r="3"/><circle cx="5" cy="19" r="3"/><circle cx="19" cy="19" r="3"/><path d="M12 8v4"/><path d="m8.5 14 3.5 2"/><path d="m15.5 14-3.5 2"/></>,
    service: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    train: <><rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16"/><path d="M12 3v8"/><circle cx="8" cy="15" r="1"/><circle cx="16" cy="15" r="1"/><path d="m9 19-2 3"/><path d="m15 19 2 3"/></>,
    plane: <><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></>,
    hotel: <><path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></>,
    building: <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></>,
    ticket: <><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></>,
    passport: <><rect x="6" y="2" width="12" height="20" rx="2"/><circle cx="12" cy="10" r="3"/><path d="M7 22h10"/><path d="M7 2h10"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.spark}</svg>;
}

function Button({ children, light = false }) {
  return <button className={`btn ${light ? "btn-light" : ""}`}>{children}<Icon name="arrow" size={17}/></button>;
}

function Eyebrow({ children }) { return <div className="eyebrow"><span/> {children}</div>; }

function SectionTitle({ label, title, copy }) {
  return <div className="section-title"><div><Eyebrow>{label}</Eyebrow><h2>{title}</h2></div>{copy && <p>{copy}</p>}</div>;
}

export default function EpicTravelTender() {
  const [openFaq, setOpenFaq] = useState(0);
  const [menu, setMenu] = useState(false);
  return <div className="site">
    <style>{css}</style>
    <header>
      <div className="header-inner">
        <a className="logo" href="#top"><img src="/untitled folder 2/image 1.png" alt="EpicTravel.ai" className="logo-img"/></a>
        <nav className={menu ? "open" : ""}><a href="#platform">Platform</a><a href="#solutions">Solutions</a><a href="#technology">Technology</a><a href="#proof">Proof</a><a href="#company">Company</a><a href="#investors">Investor Relations</a></nav>
        <div className="header-actions">
          <div className="login-dropdown">
            <span className="login-link">Log in</span>
            <div className="login-menu">
              <a href="https://visitmalaysia.ai" target="_blank" rel="noopener noreferrer">visitmalaysia.ai</a>
              <a href="https://octotravel.ai" target="_blank" rel="noopener noreferrer">octotravel.ai</a>
            </div>
          </div>
          <a className="btn" href="/contact">RFP / Tender<Icon name="arrow" size={17}/></a>
        </div>
        <button className="menu" onClick={() => setMenu(!menu)} aria-label="Toggle navigation">{menu ? "×" : "☰"}</button>
      </div>
    </header>

    <main id="top">
      <section className="hero wrap">
        <div className="hero-copy">
          <Eyebrow>AI-POWERED TRAVEL COMMERCE</Eyebrow>
          <h1>The AI commerce platform for <span className="highlight">transport,</span> <span className="highlight">tourism</span> and <span className="highlight">destinations.</span></h1>
          <p>EpicTravel.ai helps travel organisations become visible, trusted and bookable through AI — using live transport and booking data, verified local intelligence and governed enterprise workflows.</p>
          <div className="actions"><Button>Explore Epictravel</Button><button className="btn btn-outline">Invite us to your RFP / Tender <Icon name="arrow" size={17}/></button></div>
        </div>
        <div className="hero-visual">
          <img src="/untitled folder 2/Container.png" alt="AI Travel Interface"/>
        </div>
      </section>

      <section className="trust wrap">{trustItems.map(([icon, title, desc])=><div key={icon}><Icon name={icon} size={24}/><b>{title}</b><span>{desc}</span></div>)}</section>

      <section className="intro wrap" id="platform">
        <SectionTitle label="A NEW AI COMMERCE CHANNEL" title={<>AI is more than a chatbot.<br />It is a new commerce channel.</>} copy={<>Most people see only the chat window. Behind it, <strong>Epictravel</strong> connects traveller intent directly with live transport, accommodation, attraction and ticketing APIs.<br/><br/><strong>The result: Epictravel — AI that understands travel commerce.</strong> From discovery and recommendation to booking, payment and post-purchase service.</>}/>
        <div className="comparison-cards">
          <article className="comparison-card generic">
            <div className="card-label">GENERIC CHATBOTS</div>
            <h3>Answers questions</h3>
            <div className="chat-demo">
              <div className="user-msg">What's the next train from KL to Ipoh? <span className="avatar">👤</span></div>
              <div className="ai-msg"><span className="ai-badge">AI</span> I don't have access to live schedule data. Please check the official website.</div>
            </div>
            <div className="card-footer negative">ANSWERS ONLY · NO LIVE ACTION</div>
          </article>
          <article className="comparison-card epic">
            <div className="card-label teal">EPICTRAVEL.AI ENGINE</div>
            <h3>Completes the journey</h3>
            <div className="chat-demo">
              <div className="user-msg">What's the next train from KL to Ipoh? <span className="avatar teal-bg">👤</span></div>
              <div className="ai-msg epic-ai"><span className="ai-badge epic-badge">E</span> ETS Gold departs 14:00, arrives 16:25. RM35. 8 seats left. <strong>Book now?</strong></div>
            </div>
            <div className="feature-tags">
              <span>MCP & LIVE APIS</span>
              <span>REAL-TIME DATA</span>
              <span>A2A ORCHESTRATION</span>
              <span>GOVERNED BOOKING</span>
            </div>
            <div className="card-footer positive">RESULT: AI THAT UNDERSTANDS TRAVEL COMMERCE</div>
          </article>
        </div>
      </section>

      <section className="capabilities wrap">
        <SectionTitle label="ADD TO YOUR EXISTING CUSTOMER CHANNELS" title={<>Make your website, app, messaging and kiosks travel-intelligent.</>} copy="Add the same Understand → Connect → Complete the journey capability wherever customers already interact with you — without replacing the systems your organisation already operates."/>
        <div className="capabilities-grid">
          {capabilities.map(([icon, title, desc, tags]) => (
            <article key={title}>
              <Icon name={icon} size={28}/>
              <h4>{title}</h4>
              <p>{desc}</p>
              <span className="cap-tags">{tags}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="usecases wrap" id="solutions">
        <div className="usecases-header">
          <Eyebrow>WHO THIS POWERS</Eyebrow>
          <h2>Built for the organisations and creators shaping how people discover, plan, and book travel.</h2>
          <p className="usecases-subtitle">Transport operators · Tourism boards · Local councils · Destinations · Hotels · Airports &amp; transport hubs · Travel creators</p>
        </div>
        <div className="usecases-grid">
          {useCases.map((uc) => (
            <article key={uc.num} className="usecase-card">
              <div className="usecase-label">{uc.num} {uc.label}</div>
              <h3>{uc.title}</h3>
              <p>{uc.desc}</p>
              <ul className="usecase-checks">
                {uc.checks.map(c => <li key={c}><Icon name="check" size={16}/> {c}</li>)}
              </ul>
              <div className="usecase-tags">
                {uc.tags.map(t => <span key={t}>{t}</span>)}
              </div>
              <Button>{uc.btn}</Button>
            </article>
          ))}
        </div>
      </section>

      <section className="banner-new">
        <div className="banner-bg" style={{backgroundImage: "url('/untitled folder 2/imag1.png')"}}></div>
        <div className="wrap banner-content">
          <div className="banner-left">
            <Eyebrow>WHY THESE ORGANISATIONS NEED TO ACT NOW</Eyebrow>
            <h2>Travellers are shifting from static pages to AI, creators and live booking.</h2>
            <p>Travellers increasingly begin with conversational AI and creator content, then expect current information and an immediate path to booking. Organizations disconnected from these channels risk becoming invisible at the moment travel decisions are made.</p>
            <div className="stats-row">
              <div className="stat-box">
                <b>+64% YoY</b>
                <span>Growth in travellers using generative AI for tirp planning - from 11% to 18% in one year.</span>
                <small>Source: Amadeus Travel Trends 2026</small>
              </div>
              <div className="stat-box">
                <b>60% +</b>
                <span>Of travellers now cite social media as a source of travel inspiration - up from 35% in 2023.</span>
                <small>Source: Expedia Group 2025</small>
              </div>
            </div>
            <Button>Connect official APIs and creator intelligence to live transactions</Button>
          </div>
        </div>
      </section>

      <section className="destinations-section">
        <div className="wrap">
          <div className="destinations-header">
            <div className="destinations-header-left">
              <Eyebrow>DESTINATION AI IN ACTION</Eyebrow>
              <h2>Make your destination discoverable, trusted, and bookable through AI.</h2>
            </div>
            <div className="destinations-header-right">
              <p>Epictravel.ai gives tourism boards one governed destination platform for creator-led discovery, official visitor information, live local services, and measurable bookings.</p>
            </div>
          </div>
          <div className="destination-layout">
            <div className="destination-image">
              <img src="/untitled folder 2/BrowserMockup.png" alt="VisitMalaysia.ai Platform"/>
            </div>
            <div className="destination-info-card">
              <div className="dest-label">FOR TOURISM MINISTRIES AND BOARDS</div>
              <h3>Visitmalaysia.ai</h3>
              <p>An AI-powered national destination platform combining creator content, official tourism info, and live visitor guidance.</p>
              <div className="dest-features">
                <span>MODERNISE STATIC DESTINATION CONTENT</span>
                <span>PROMOTE LICENSED & RECOGNISED BUSINESS</span>
                <span>TURN INSPIRATION INTO ITINERARIES & ACTION</span>
              </div>
              <Button>Build an AI destination platform</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="how-epic-works">
        <div className="wrap">
          <div className="how-epic-header">
            <div className="how-epic-header-left">
              <Eyebrow>HOW EPICTRAVEL.AI WORKS</Eyebrow>
              <h2>From creator inspiration to measurable visitor action.</h2>
            </div>
            <div className="how-epic-header-right">
              <p>Three connected capabilities move visitors from discovery to a current, trusted, and bookable destination experience.</p>
            </div>
          </div>
          <div className="how-epic-cards">
            <article className="how-epic-card">
              <div className="how-epic-num">01</div>
              <h3>Inspire & understand</h3>
              <p>Understand what inspires visitors through current creator recommendations, grounded in official destination information and licensed local businesses.</p>
              <div className="how-epic-visual">
                <img src="/untitled folder 2/Container (2).png" alt=""/>
              </div>
              <div className="how-epic-tag">TRUSTED AND GROUNDED AT THE SOURCE</div>
            </article>
            <article className="how-epic-card">
              <div className="how-epic-num">02</div>
              <h3>Access live Malaysia data</h3>
              <p>Connect visitor intent through Epictravel.ai's propriety integration layer to live transport, fares, availability, accommodation, attractions, and destinations services across Malaysia.</p>
              <div className="how-epic-visual">
                <img src="/untitled folder 2/ConnectVisual.png" alt=""/>
              </div>
              <div className="how-epic-tag">LIVE MOBILITY & INVENTORY</div>
            </article>
            <article className="how-epic-card">
              <div className="how-epic-num">03</div>
              <h3>Book & measure</h3>
              <p>Move visitors from inspiration to personalized itineraries, bookings, and referrals, while measuring engagement and all the local tourism activities.</p>
              <div className="how-epic-visual">
                <img src="/untitled folder 2/CompleteVisual.png" alt=""/>
              </div>
              <div className="how-epic-tag">ONE GOVERNED JOURNEY</div>
            </article>
          </div>
        </div>
      </section>

      <section className="tourism-cta">
        <div className="wrap tourism-cta-inner">
          <h2>Build an AI destination platform for your tourism board.</h2>
          <Button>Discuss your destination AI programme</Button>
        </div>
      </section>

      <section className="market-shift">
        <div className="wrap">
          <div className="market-shift-header">
            <div className="market-shift-left">
              <Eyebrow>BUILT FOR THIS MARKET SHIFT</Eyebrow>
              <h2>The travel AI capabilities organisations need to stay connected.</h2>
            </div>
            <div className="market-shift-right">
              <p>Epictravel.ai connects existing transport, tourism, and commerce systems to the new channels where travellers discover, decide, and book.</p>
            </div>
          </div>
          <div className="market-shift-cards">
            <article className="market-shift-card">
              <div className="ms-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></div>
              <h3>Enterprise integration</h3>
              <p>Connect live mobility, inventory and existing organisational systems.</p>
              <ul className="ms-checks">
                <li><Icon name="check" size={14}/> GTFS / GTFS-RT</li>
                <li><Icon name="check" size={14}/> Live booking APIs</li>
                <li><Icon name="check" size={14}/> Unified API gateway</li>
                <li><Icon name="check" size={14}/> Legacy interoperability</li>
              </ul>
            </article>
            <article className="market-shift-card">
              <div className="ms-icon"><Icon name="network" size={24}/></div>
              <h3>Agentic AI architecture</h3>
              <p>Coordinate specialised agents, tools and customer channels.</p>
              <ul className="ms-checks">
                <li><Icon name="check" size={14}/> Model Context Protocol (MCP)</li>
                <li><Icon name="check" size={14}/> Agent-to-Agent (A2A)</li>
                <li><Icon name="check" size={14}/> Omnichannel AI</li>
                <li><Icon name="check" size={14}/> Human-in-the-loop</li>
              </ul>
            </article>
            <article className="market-shift-card">
              <div className="ms-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg></div>
              <h3>Governance & security</h3>
              <p>Keep AI decisions controlled, explainable and accountable.</p>
              <ul className="ms-checks">
                <li><Icon name="check" size={14}/> Explainable AI</li>
                <li><Icon name="check" size={14}/> RBAC</li>
                <li><Icon name="check" size={14}/> Audit logging</li>
                <li><Icon name="check" size={14}/> PDPA-aligned governance</li>
              </ul>
            </article>
            <article className="market-shift-card">
              <div className="ms-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg></div>
              <h3>Reliability & commerce</h3>
              <p>Operate at scale and connect assistance with powerful, measurable value.</p>
              <ul className="ms-checks">
                <li><Icon name="check" size={14}/> High availability</li>
                <li><Icon name="check" size={14}/> Agentic travel commerce</li>
                <li><Icon name="check" size={14}/> Closed-loop attribution</li>
                <li><Icon name="check" size={14}/> UCP / AP2-compatible</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="orchestration-section">
        <div className="orchestration-bg" style={{backgroundImage: "url('/untitled folder 2/imag1.png')"}}></div>
        <div className="wrap orchestration-content">
          <div className="orchestration-grid">
            <div className="orchestration-left">
              <div className="orchestration-card-main">
                <Eyebrow>MCP & AUTONOMOUS ORCHESTRATION</Eyebrow>
                <h2>Unified AI orchestration across all your APIs and legacy systems.</h2>
                <p>Turn one traveller request into clear tasks, connects the right approval systems and keeps every specialist working towards one accountable result.</p>
                <div className="orchestration-tags">
                  <span>REST API</span>
                  <span>LLM ROUTING</span>
                  <span>POLICY CONTROL</span>
                </div>
              </div>
            </div>
            <div className="orchestration-right">
              <div className="agent-card">
                <span className="agent-num">01</span>
                <div className="agent-content">
                  <h4>Journey & Ticketing Agent</h4>
                  <p>Parses itinerary structure, validates fare rules for the given route, and requests confirmed seat and ticket issuance.</p>
                </div>
                <span className="agent-badge">API</span>
              </div>
              <div className="agent-card">
                <span className="agent-num">02</span>
                <div className="agent-content">
                  <h4>Travel Recommendation Agent</h4>
                  <p>Recommends hotels, food, and attractions using official information and current creator content.</p>
                </div>
                <span className="agent-badge">MCP</span>
              </div>
              <div className="agent-card">
                <span className="agent-num">03</span>
                <div className="agent-content">
                  <h4>Booking & Purchase Agent</h4>
                  <p>Checks live availability, presents relevant offers and complete authorised bookings or purchases.</p>
                </div>
                <span className="agent-badge">A2A</span>
              </div>
              <div className="agent-card">
                <span className="agent-num">04</span>
                <div className="agent-content">
                  <h4>Customer Service Agent</h4>
                  <p>Sends updates, handles changes or disruptions and transfers the case to staff when human help is needed.</p>
                </div>
                <span className="agent-badge">HTL</span>
              </div>
            </div>
          </div>
          <div className="orchestration-features">
            <article>
              <span>01</span>
              <h4>Regional Travel Graph</h4>
              <p>Connects transport, stays, destinations, attractions, creators, and official information.</p>
            </article>
            <article>
              <span>02</span>
              <h4>MCP + A2A</h4>
              <p>Approved tools and specialised agents collaborate across complex traveller journeys.</p>
            </article>
            <article>
              <span>03</span>
              <h4>Agentic Commerce</h4>
              <p>Architecture designed for UCP, AP2, and secure transaction routing.</p>
            </article>
            <article>
              <span>04</span>
              <h4>Governed Execution</h4>
              <p>Source controls, consent, audit trails, and human escalation keep actions accountable.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="omnipresence-section">
        <div className="wrap">
          <div className="omnipresence-grid">
            <div className="omnipresence-left">
              <Eyebrow>OMNIPRESENCE</Eyebrow>
              <h2>Always present. One traveller context across every channel.</h2>
              <p>Three connected capabilities move visitors from discovery to a current, trusted, and bookable destination experience.</p>
              <div className="omnipresence-tags">
                <span>WEBSITE</span>
                <span>MOBILE APP</span>
                <span>WHATSAPP</span>
                <span>EMAIL</span>
                <span>CRM & SERVICE DESK</span>
                <span>SOCIAL MESSAGING</span>
                <span>STATION KIOSK</span>
                <span>VOICE & CALL CENTRE</span>
              </div>
            </div>
            <div className="omnipresence-right">
              <img src="/untitled folder 2/Group 7.png" alt="One continuous conversation across all channels"/>
              {/* <div className="omnipresence-flow">
                <span>Plan on web</span>
                <span className="flow-arrow">→</span>
                <span>Continue on Whatsapp</span>
                <span className="flow-arrow">→</span>
                <span>Resolve with an agent</span>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <section className="observability-section">
        <div className="wrap">
          <div className="observability-header">
            <div className="observability-header-left">
              <Eyebrow>AI OBSERVABILITY & EVALUATION PLATFORM</Eyebrow>
              <h2>One integrated platform to observe, evaluate, and govern your travel AI.</h2>
            </div>
            <div className="observability-header-right">
              <p>Trace every response, source, and system action. Evaluate answer quality, compare prompt and model versions, monitor performance, and route sensitive decisions for human approval - all from one governed platform.</p>
            </div>
          </div>
          <div className="observability-grid">
            <div className="observability-steps">
              <article className="obs-step">
                <span>01</span>
                <div>
                  <h4>Observe</h4>
                  <p>Prompts, responses, tool calls, latency, available, and cost.</p>
                </div>
              </article>
              <article className="obs-step">
                <span>02</span>
                <div>
                  <h4>Evaluate</h4>
                  <p>Grounding quality, source relevance, policy compliance, and business outcomes.</p>
                </div>
              </article>
              <article className="obs-step">
                <span>03</span>
                <div>
                  <h4>Govern</h4>
                  <p>RBAC, approved resources, confidence thresholds, guardrails, and human approval.</p>
                </div>
              </article>
              <article className="obs-step">
                <span>04</span>
                <div>
                  <h4>Improve</h4>
                  <p>Review failed journeys, refine workflows, test changes, and compare versions.</p>
                </div>
              </article>
            </div>
            <div className="observability-diagram">
              <img src="/untitled folder 2/Container (5).png" alt="Epictravel Trust Layer"/>
            </div>
          </div>
          <div className="observability-features">
            <article>
              <h4>Knowledge Sync</h4>
              <p>Connect approved schedules, policies, inventory, and content into a verified enterprise fact base.</p>
            </article>
            <article>
              <h4>Intent Evaluation</h4>
              <p>Understand the full request, separate multiple needs, and select the appropriate operational path.</p>
            </article>
            <article>
              <h4>Policy Governance</h4>
              <p>Apply confidence thresholds, access controls, language rules, guardrails, and human hand-off policies.</p>
            </article>
            <article>
              <h4>Commercial Execution</h4>
              <p>Complete or route bookings, service actions, referrals, packages, and relevant partner offers.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="guardrails-section">
        <div className="wrap">
          <div className="guardrails-header">
            <div className="guardrails-header-left">
              <Eyebrow>PRODUCTION DEPLOYMENT ASSURANCE</Eyebrow>
              <h2>Guardrails for accuracy, security, and dependable operation.</h2>
            </div>
            <div className="guardrails-header-right">
              <p>Our specialist architecture combines grounded retrieval, role-based controls, observability, and resilient integration patterns for national transport, tourism, and destination-scale deployments.</p>
              <Button>Request technical capability matrix</Button>
            </div>
          </div>
          <div className="guardrails-grid">
            <div className="guardrails-cards">
              <article className="guardrail-card">
                <span className="guardrail-label">ACCURACY</span>
                <h4>Grounded responses & safety brakes</h4>
                <p>Approved sources, confidence thresholds, live verification and fallback paths reduce false fare, route and schedule responses.</p>
                <div className="guardrail-tag">HALLUCINATION RISK CONTROLS</div>
              </article>
              <article className="guardrail-card">
                <span className="guardrail-label">SECURITY</span>
                <h4>Security & data isolation</h4>
                <p>PDPA-aligned data controls, role-based access, audit records and deployment-specific data requirements.</p>
                <div className="guardrail-tag">DATA GOVERNANCE & SOVEREIGNTY</div>
              </article>
              <article className="guardrail-card">
                <span className="guardrail-label">RELIABILITY</span>
                <h4>High availability architecture</h4>
                <p>Designed for high passenger volumes, peak travel demand, concurrent usage and agreed service levels.</p>
                <div className="guardrail-tag">TRANSPORT-SCALE INFRASTRUCTURE</div>
              </article>
              <article className="guardrail-card">
                <span className="guardrail-label">GOVERNANCE</span>
                <h4>Controlled phased rollout</h4>
                <p>Human oversight, fallback triggers and measurable operational benchmarks before each expansion stage.</p>
                <div className="guardrail-tag">HUMAN OVERSIGHT & ESCALATION</div>
              </article>
            </div>
            <div className="guardrails-visual">
              <div className="guardrails-visual-bg" style={{backgroundImage: "url('/untitled folder 2/b112.png')"}}></div>
              <div className="control-stack">
                <div className="control-stack-header">Epictravel.ai · AI Control Stack</div>
                <div className="control-stack-content">
                  <div className="control-row"><span className="control-key">Endpoint:</span> /api/travel/intent</div>
                  <div className="control-row"><span className="control-key">Status:</span> <span className="control-success">200 OK</span></div>
                  <div className="control-row"><span className="control-key">Grounding:</span> <span className="control-success">Verified</span> · 3 sources</div>
                  <div className="control-row"><span className="control-key">Policy:</span> Compliant</div>
                </div>
                <div className="control-checks">
                  <div><span className="check-dot green"></span> Sources verified</div>
                  <div><span className="check-dot green"></span> RBAC check passed</div>
                  <div><span className="check-dot green"></span> Confidence ≥ 0.87</div>
                </div>
                <div className="control-footer">
                  <span className="delivery-badge">Delivery restored · Compliant</span>
                </div>
              </div>
            </div>
          </div>
          <p className="guardrails-note">Security, residency, availability and compliance requirements are confirmed for each deployment and reflected in the applicable technical and service-level agreements.</p>
        </div>
      </section>

      <section className="modular-section">
        <div className="wrap">
          <div className="modular-header">
            <div className="modular-header-left">
              <Eyebrow>MODULAR DEPLOYMENT MENU</Eyebrow>
              <h2>Start with one modular AI agent. Scale as you grow.</h2>
            </div>
            <div className="modular-header-right">
              <p>Deploy the exact agent module your travellers need today. As operational priorities expand, plug additional channels, and merchant networks into the same governed platform without rebuilding your infrastructure.</p>
            </div>
          </div>
          <div className="modular-agents">
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">RAILWAY & TRANSIT</span>
                <span className="agent-module-tag">LIVE API DATA</span>
              </div>
              <div className="agent-module-icon"><Icon name="train" size={28}/></div>
              <h4>Transit & Journey Agent</h4>
              <p>Automates live schedules, disruption alerts, route planning, and instant ticket bookings in chat.</p>
            </article>
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">AIRPORTS & TERMINAL</span>
                <span className="agent-module-tag">WAYFINDING</span>
              </div>
              <div className="agent-module-icon"><Icon name="plane" size={28}/></div>
              <h4>Airport & Hub Assistant</h4>
              <p>Guides passengers with terminal way finding, gate updates, retail discovery, and baggage support.</p>
            </article>
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">HOSPITALITY</span>
                <span className="agent-module-tag">ANCILLARY REVENUE</span>
              </div>
              <div className="agent-module-icon"><Icon name="hotel" size={28}/></div>
              <h4>Hotel & Resort Concierge</h4>
              <p>Handles guest questions, local recommendations, room upgrades, and bookable partner experiences.</p>
            </article>
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">TOURISM BOARDS & GOVERNMENT</span>
                <span className="agent-module-tag">VISITOR GUIDANCE</span>
              </div>
              <div className="agent-module-icon"><Icon name="building" size={28}/></div>
              <h4>Destination Agent</h4>
              <p>Delivers official destination intelligence, itinerary planning, and creator-grounded travel guidance.</p>
            </article>
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">ATTRACTIONS & EXPERIENCES</span>
                <span className="agent-module-tag">BOOKABLE EXPERIENCES</span>
              </div>
              <div className="agent-module-icon"><Icon name="ticket" size={28}/></div>
              <h4>Destination Agent</h4>
              <p>Surfaces nearby attractions, events, and experience with live availability, and instant booking confirmation.</p>
            </article>
            <article className="agent-module">
              <div className="agent-module-header">
                <span className="agent-module-label">TRAVEL SERVICES</span>
                <span className="agent-module-tag">PRE-TRIP WORKFLOW</span>
              </div>
              <div className="agent-module-icon"><Icon name="passport" size={28}/></div>
              <h4>Travel Service Agent</h4>
              <p>Coordinates visa, insurance, currency exchange, and travel support services into a seamless pre-trip workflow.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Built From Real Traveller Intent Section - Right after Modular Deployment */}
      <section className="intent-section">
        <div className="intent-bg" style={{backgroundImage: "url('/untitled folder 2/back11.png')"}}></div>
        <div className="wrap intent-content">
          <div className="intent-left">
            <Eyebrow>BUILT FROM REAL TRAVELLER INTENT</Eyebrow>
            <h2>Built on 2M+ Southeast Asian travel intent signals.</h2>
            <p>Powers localised conversion, automated passenger support and agentic booking workflows across regional transport and destination networks. Anonymised interaction data across regional routes, fares, and creator insights continuously strengthens our proprietary intelligence layer.</p>
            <Button>Proprietary Regional Travel Graph</Button>
          </div>
          <div className="intent-right">
            <div className="intent-stats">
              <article className="intent-stat">
                <b>≈2M</b>
                <h4>Real travel questions & answers</h4>
                <p>Strengthening our understanding of regional traveller intent.</p>
              </article>
              <article className="intent-stat">
                <b>SEA</b>
                <h4>Local language and journey patterns</h4>
                <p>Routes, destinations, timing, budgets, and service needs across Southeast Asia.</p>
              </article>
              <article className="intent-stat">
                <b>800 +</b>
                <h4>Travel creators & local experts</h4>
                <p>A fast-growing network supplying current, firsthand destination knowledge across Southeast Asia.</p>
              </article>
              <article className="intent-stat">
                <b>Live Data</b>
                <h4>Connected regional travel intelligence</h4>
                <p>Official transport, booking, destination, and creator data in one governed layer.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Growing Travel Data Network Section */}
      <section className="network-section">
        <div className="wrap">
          <Eyebrow>A GROWING TRAVEL DATA NETWORK</Eyebrow>
          <h2>More partners. Better travel intelligence. More transactions.</h2>
          <div className="network-flow">
            <span>More travel partners</span>
            <span className="flow-arrow">→</span>
            <span>More live & local data</span>
            <span className="flow-arrow">→</span>
            <span>Better recommendations</span>
            <span className="flow-arrow">→</span>
            <span>More completed journeys</span>
          </div>
          <p className="network-desc">Every transport operator adds live journey data. Every tourism organisation adds official destination information. Every creator adds current, real-world recommendations. Together, they help EpicTravel.ai deliver better answers, connect more services, and complete more bookings.</p>
        </div>
      </section>

      {/* Team Behind the System Section */}
      <section className="team-section">
        <div className="wrap">
          <div className="team-grid">
            <div className="team-left">
              <Eyebrow>THE TEAM BEHIND THE SYSTEM</Eyebrow>
              <h2>Built by a team of industry veterans across travel, AI, and public-sector digital infrastructure.</h2>
              <p>The EpicTravel.ai system is designed and delivered by specialists with experiences in tourism, AI research, enterprise engineering, government digital programmes, and commercial growth.</p>
              <div className="team-tags">
                <span>SOUTHEAST ASIA</span>
                <span>ENTERPRISE AI</span>
                <span>GOVERNMENT DIGITAL</span>
                <span>TRAVEL COMMERCE</span>
              </div>
            </div>
            <div className="team-right">
              <div className="team-cards">
                <article className="team-card">
                  <h4>Travel & Tourism</h4>
                  <p>Decades of destination, tourism-board, and travel-sector experience.</p>
                </article>
                <article className="team-card">
                  <h4>AI Research</h4>
                  <p>Explainable AI, graph intelligence, and secure multimodal discovery.</p>
                </article>
                <article className="team-card">
                  <h4>Enterprise Engineering</h4>
                  <p>Experienced architecture and AI application leaders with regional delivery depth.</p>
                </article>
                <article className="team-card">
                  <h4>Public-Sector & Commercial Delivery</h4>
                  <p>Government partnerships, product, and growth experience across regional, and global platforms.</p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div className="faq-left">
              <Eyebrow>FREQUENTLY ASKED QUESTIONS</Eyebrow>
              <h2>F.A.Q.</h2>
              <p>Key technical, commercial, and deployment answers for enterprise IT, procurement, and leadership teams.</p>
            </div>
            <div className="faq-right">
              {faqs.map(([q, a], i) => (
                <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  <div className="faq-question">
                    <span>{q}</span>
                    <span className="faq-toggle">{openFaq === i ? '−' : '+'}</span>
                  </div>
                  {openFaq === i && <div className="faq-answer">{a}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Long-Term Enterprise Partner Section */}
      <section className="enterprise-section">
        <div className="wrap">
          <div className="enterprise-grid">
            <div className="enterprise-left">
              <Eyebrow>LONG-TERM ENTERPRISE PARTNER</Eyebrow>
              <h2>Built to support critical travel services for the long term.</h2>
              <p>EpicTravel.ai combines specialist travel technology, enterprise engineering, and governed AI operations. Its modular architecture, documented integration standards and controlled deployment model help organisations modernise customer services without creating dependence on a closed system.</p>
              <Button>Discuss a long-term technology partnership</Button>
            </div>
            <div className="enterprise-right">
              <div className="enterprise-features">
                <div className="enterprise-feature">
                  <span>Phased deployment</span>
                </div>
                <div className="enterprise-feature">
                  <span>Documented APIs & interoperability</span>
                </div>
                <div className="enterprise-feature">
                  <span>Enterprise support & continuity</span>
                </div>
                <div className="enterprise-feature">
                  <span>Human oversight & governance</span>
                </div>
                <div className="enterprise-feature">
                  <span>Knowledge transfer</span>
                </div>
                <div className="enterprise-feature">
                  <span>Long-term product roadmap</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - The Shift Has Started */}
      <section className="shift-section">
        <div className="wrap">
          <div className="shift-grid">
            <div className="shift-left">
              <div className="shift-eyebrow">THE SHIFT HAS STARTED</div>
              <h2>Contact us to make your organisation AI-ready.</h2>
            </div>
            <div className="shift-right">
              <button className="btn btn-dark">Become AI-ready <Icon name="arrow" size={14}/></button>
              <div className="shift-links">
                <a href="#rfp">Invite us to your  RFP / Tender <span className="shift-arrow">↗</span></a>
                <a href="#capability">Request technical capability matrix <span className="shift-arrow">↗</span></a>
                <a href="#whitepaper">Request technical whitepaper <span className="shift-arrow">↗</span></a>
              </div>
              <div className="shift-tags">
                Enterprise deployment · Tender participation · Government programmes · Creator partnerships
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>

    <footer>
      <div className="wrap footer-grid">
        <div className="footer-brand">
          <a className="logo" href="#top"><img src="/untitled folder 2/image 1.png" alt="EpicTravel.ai" className="logo-img"/></a>
          <p>Enterprise agentic infrastructure for a world moving from search to AI.</p>
        </div>
        <div className="footer-links">
          <div className="footer-col">
            <b>EXPLORE</b>
            <a href="#platform">Platform</a>
            <a href="#solutions">Solutions</a>
            <a href="#technology">Technology</a>
            <a href="#proof">Proof</a>
          </div>
          <div className="footer-col">
            <b>ENGAGE</b>
            <a href="/contact">Invite us to your RFP / Tender</a>
            <a href="#whitepaper">Technical whitepaper</a>
            <a href="#investors">Investor relations</a>
          </div>
        </div>
      </div>
      <div className="wrap copyright">
        <span>© 2026 EpicTravel AI Sdn. Bhd.</span>
        <span className="copyright-location">Malaysia · Southeast Asia</span>
      </div>
    </footer>
  </div>;
}

const css = `html,body{scrollbar-width:none;-ms-overflow-style:none}html::-webkit-scrollbar,body::-webkit-scrollbar{display:none}
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@500;600&display=swap');

/* ===== DESIGN SYSTEM: Spacing Scale ===== */
/* 8, 16, 24, 32, 48, 64, 80, 100, 120px */

*{box-sizing:border-box}
html{scroll-behavior:smooth}
body{margin:0}

.site{font-family:'DM Sans',sans-serif;color:${C.ink};background:#fff;line-height:1.6}

/* ===== CONSISTENT CONTAINER ===== */
.wrap{width:100%;max-width:1200px;margin:0 auto;padding-left:20px;padding-right:20px;padding-top:0;padding-bottom:0;box-sizing:border-box}
section .wrap,main .wrap{padding-left:20px;padding-right:20px}

/* ===== TYPOGRAPHY ===== */
h1,h2{font-family:'Playfair Display',serif;font-weight:500;line-height:1.05;letter-spacing:-.035em;margin:0}
h1{font-size:clamp(48px,5.5vw,72px)}
h1 .highlight{background:linear-gradient(to top,${C.aqua} 35%,transparent 35%);padding:0 4px}
h2{font-size:clamp(32px,4vw,52px)}
h3{font-size:20px;line-height:1.25;margin:0 0 12px}
p{color:#5d6864;margin:0}
a{color:inherit;text-decoration:none}
button{font:inherit}

/* ===== EYEBROW ===== */
.eyebrow{display:flex;align-items:center;gap:10px;font-size:11px;font-weight:700;letter-spacing:.15em;color:#178b8d;margin-bottom:24px;justify-content:flex-start}
.eyebrow span{display:none}

/* ===== HEADER ===== */
header{height:72px;width:100%;border-bottom:1px solid ${C.line};position:relative;z-index:10;background:#fff}
.header-inner{height:100%;width:100%;max-width:1200px;margin:0 auto;padding:0 20px;display:flex;align-items:center;box-sizing:border-box}
.logo{display:flex;align-items:center;flex-shrink:0}
.logo-img{height:32px;width:auto;display:block}
.footer-logo{filter:brightness(0) invert(1)}
nav{display:flex;align-items:center;gap:32px;font-size:13px;font-weight:500;margin-left:48px}
nav a{transition:color .2s}
nav a:hover{color:#20aaad}
.header-actions{display:flex;align-items:center;gap:24px;flex-shrink:0;margin-left:auto}
.login-dropdown{position:relative}
.login-link{font-size:13px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:6px}
.login-link::after{content:'';width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid ${C.ink};transition:transform 0.2s}
.login-dropdown:hover .login-link::after{transform:rotate(180deg)}
.login-menu{position:absolute;top:calc(100% + 8px);left:50%;transform:translateX(-50%) scale(0.95);background:#fff;border-radius:12px;box-shadow:0 4px 24px rgba(0,0,0,0.12),0 0 0 1px rgba(0,0,0,0.05);min-width:200px;padding:8px;opacity:0;visibility:hidden;transition:all 0.15s ease;z-index:100}
.login-dropdown:hover .login-menu{opacity:1;visibility:visible;transform:translateX(-50%) scale(1)}
.login-menu a{display:flex;align-items:center;gap:12px;padding:10px 12px;font-size:14px;font-weight:500;color:${C.ink};border-radius:8px;transition:background 0.15s}
.login-menu a:hover{background:#f5f5f5}
.login-menu a::before{content:'🌐';font-size:16px}

/* ===== BUTTONS ===== */
.btn{border:0;border-radius:99px;padding:14px 24px;background:${C.ink};color:#fff;display:inline-flex;align-items:center;justify-content:center;gap:12px;cursor:pointer;font-size:13px;font-weight:600;line-height:1;white-space:nowrap;transition:background .2s}
.btn:hover{background:#178b8d}
.btn-light{background:#fff;color:${C.ink}}
.btn-outline{background:transparent;color:${C.ink};border:1.5px solid ${C.ink}}
.btn-outline:hover{background:${C.ink};color:#fff}
.menu{display:none;background:none;border:0;font-size:24px;padding:8px}

/* ===== HERO SECTION ===== */
.hero{padding:80px 0;display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.hero-copy{text-align:left;display:flex;flex-direction:column;justify-content:flex-start;align-self:stretch}
.hero-copy>p{font-size:18px;max-width:540px;margin:24px 0 0}
/* Buttons follow the paragraph directly. They used to be pushed to the bottom of
   the row with margin-top:auto, which left a gap under the shorter two-line H1. */
.actions{display:flex;align-items:center;gap:16px;justify-content:flex-start;margin-top:auto;padding-top:36px}
   .actions .btn,.actions .btn-outline{padding:14px 24px}
/* ===== HERO VISUAL ===== */
.hero-visual{display:flex;align-items:center;justify-content:flex-end}
.hero-visual img{width:90%;max-width:100%;height:auto;max-height:635px;border-radius:16px;object-fit:cover;object-position:top}

/* ===== TRUST/FEATURE ITEMS ===== */
.trust{display:grid;grid-template-columns:repeat(4,1fr);gap:0;padding:32px 0;border-top:1px solid ${C.line};border-bottom:1px solid ${C.line};margin-top:48px}
.trust div{padding:0 24px;border-right:1px solid ${C.line};display:flex;flex-direction:column;align-items:flex-start}
.trust div:first-child{padding-left:0}
.trust div:last-child{border-right:0;padding-right:0}
.trust svg{color:${C.ink};margin-bottom:12px;flex-shrink:0}
.trust b{font-size:14px;font-weight:600;margin-bottom:6px;display:block}
.trust span{font-size:12px;color:#73807c;line-height:1.5;display:block}

/* ===== SECTION SPACING ===== */
.intro,.platform,.usecases,.mobile,.deployment,.credentials,.how,.faq{padding:100px 0}

/* ===== SECTION TITLE ===== */
.section-title{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:start;margin-bottom:64px;text-align:left}
@media screen and (max-width: 768px) {
  .eyebrow {
    margin-top: 64px;
  }
}
.section-title>div{max-width:100%;text-align:left}
.section-title p{font-size:17px;line-height:1.7;text-align:left}

/* ===== COMPARISON CARDS ===== */
.comparison-cards{display:grid;grid-template-columns:0.8fr 1.2fr;gap:24px;margin-top:48px}
.comparison-card{background:#f8f8f6;border-radius:20px;padding:40px;display:flex;flex-direction:column;min-height:480px}
.comparison-card.generic{border-radius:16px}
.comparison-card.epic{border:2px solid ${C.ink};background:#fff;border-radius:16px}
.card-label{font-size:11px;font-weight:700;letter-spacing:.12em;color:#666;margin-bottom:20px}
.card-label.teal{color:#178b8d}
.comparison-card h3{font-family:'Playfair Display',serif;font-size:28px;font-weight:500;margin:0 0 24px}
.chat-demo{flex:1;display:flex;flex-direction:column;justify-content:flex-start;padding:16px 0}
.user-msg{background:#fff;border-radius:20px;padding:14px 18px;display:inline-flex;align-items:center;gap:12px;font-size:14px;margin-bottom:16px;box-shadow:0 2px 8px rgba(0,0,0,0.06);max-width:90%}
.user-msg .avatar{width:28px;height:28px;border-radius:50%;background:#e0e0e0;display:flex;align-items:center;justify-content:center;font-size:14px}
.user-msg .avatar.teal-bg{background:${C.teal}}
.ai-msg{display:flex;gap:12px;align-items:flex-start;font-size:14px;color:#555;padding:12px 0}
.ai-badge{width:28px;height:28px;border-radius:50%;background:#e8e8e8;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#888;flex-shrink:0}
.ai-badge.epic-badge{background:${C.teal};color:#fff}
.ai-msg.epic-ai{background:#fff;padding:14px 18px;border-radius:12px;border-left:3px solid ${C.teal}}
.feature-tags{display:flex;flex-wrap:wrap;gap:10px;margin:24px 0}
.feature-tags span{background:#fff;border:1px solid #ddd;padding:8px 14px;border-radius:20px;font-size:10px;font-weight:600;letter-spacing:.05em}
.card-footer{padding:16px 20px;border-radius:10px;font-size:11px;font-weight:700;letter-spacing:.08em;margin-top:auto;text-align:center}
.card-footer.negative{background:#e8e8e8;color:#666}
.card-footer.positive{background:${C.ink};color:#fff}

/* ===== CAPABILITIES SECTION ===== */
.capabilities{padding:100px 0}
.capabilities-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.capabilities-grid article{background:#fff;border:1px solid ${C.line};border-radius:16px;padding:28px;display:flex;flex-direction:column;transition:all .2s}
.capabilities-grid article.highlighted{border:2px solid ${C.teal}}
.capabilities-grid article svg{color:${C.ink};margin-bottom:16px;flex-shrink:0}
.capabilities-grid h4{font-size:16px;font-weight:600;margin:0 0 12px;line-height:1.3}
.capabilities-grid p{font-size:13px;color:#5d6864;flex:1;margin:0 0 16px}
.cap-tags{font-size:10px;font-weight:700;letter-spacing:.08em;color:${C.ink};margin-top:auto;padding-top:16px}

/* ===== USE CASES / AUDIENCE CARDS ===== */
.usecases{padding:100px 0}
.usecases-header{margin-bottom:64px;text-align:left;width:100%}
.usecases-header h2{max-width:900px;margin:0 0 20px}
.usecases-subtitle{font-size:15px;color:#5d6864}
.usecases-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.usecase-card{border-radius:20px;padding:32px;display:flex;flex-direction:column;height:100%;background:#fff;border:1px solid #e8e8e6}
.usecase-card:nth-child(1){background:#e8f5f4;border-color:#d0e8e6}
.usecase-card:nth-child(2){background:#f5f5f3;border-color:#e8e8e6}
.usecase-card:nth-child(3){background:#fff;border-color:#e8e8e6}
.usecase-label{font-size:10px;font-weight:700;letter-spacing:.1em;color:#666;margin-bottom:20px}
.usecase-card h3{font-family:'Playfair Display',serif;font-size:24px;font-weight:500;line-height:1.25;margin:0 0 20px;color:${C.ink}}
.usecase-card>p{font-size:14px;color:#5d6864;line-height:1.6;margin-bottom:24px}
.usecase-checks{list-style:none;padding:0;margin:0 0 24px}
.usecase-checks li{display:flex;align-items:flex-start;gap:10px;font-size:13px;margin-bottom:12px;color:${C.ink}}
.usecase-checks svg{color:${C.ink};flex-shrink:0;margin-top:3px}
.usecase-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:24px;margin-top:auto}
.usecase-tags span{background:#fff;border:1px solid #ddd;padding:8px 14px;border-radius:6px;font-size:10px;font-weight:600;letter-spacing:.05em;color:${C.ink}}
.usecase-card .btn{margin-top:24px;align-self:flex-start;background:${C.ink};color:#fff}

/* ===== BANNER SECTION ===== */
.banner-new{position:relative;min-height:560px;overflow:hidden}
.banner-bg{position:absolute;inset:0;background-size:cover;background-position:center right}
.banner-content{position:relative;z-index:2;padding:100px 0}
.banner-left{max-width:560px;text-align:left}
.banner-left .eyebrow{color:#178b8d}
.banner-left .btn{align-self:flex-start}
.banner-left h2{font-size:clamp(32px,4vw,48px);line-height:1.1;margin-bottom:24px;color:${C.ink}}
.banner-left>p{font-size:16px;line-height:1.7;margin-bottom:32px}
.stats-row{display:flex;gap:24px;margin-bottom:32px;max-width:480px}
.stat-box{background:rgba(255,255,255,0.92);backdrop-filter:blur(10px);padding:24px;border-radius:12px;flex:1;text-align:left}
.stat-box b{font-family:'Playfair Display',serif;font-size:32px;display:block;margin-bottom:12px;color:${C.ink}}
.stat-box span{font-size:13px;color:#5d6864;display:block;line-height:1.5}
.stat-box small{font-size:11px;color:#999;margin-top:12px;display:block}

/* ===== DESTINATIONS SECTION ===== */
.destinations-section{padding:100px 0;background:#f8f8f6}
.destinations-header{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:start;margin-bottom:64px}
.destinations-header-left{text-align:left}
.destinations-header-left h2{font-size:clamp(32px,4vw,48px);margin-top:20px}
.destinations-header-right{padding-top:40px;text-align:left}
.destinations-header-right p{font-size:16px;line-height:1.7;color:#5d6864}
.destination-layout{display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:center}
.destination-image{border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.12)}
.destination-image img{width:100%;height:auto;display:block}
.destination-info-card{background:#fff;border-radius:16px;padding:40px;box-shadow:0 4px 20px rgba(0,0,0,0.06)}
.dest-label{font-size:11px;font-weight:700;letter-spacing:.12em;color:#178b8d;margin-bottom:16px}
.destination-info-card h3{font-family:'Playfair Display',serif;font-size:28px;font-weight:500;margin:0 0 16px;font-style:italic}
.destination-info-card>p{font-size:14px;color:#5d6864;line-height:1.6;margin-bottom:24px}
.dest-features{display:flex;flex-direction:column;gap:12px;margin-bottom:28px}
.dest-features span{background:#f8f8f6;border:1px solid #e8e8e6;border-radius:8px;padding:12px 18px;font-size:11px;font-weight:600;letter-spacing:.05em;color:#333}

/* ===== HOW EPIC WORKS SECTION ===== */
.phone-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;text-align:center}
.phone{height:475px;width:245px;max-width:100%;margin:auto;background:#f5f5f0;border:8px solid #17201e;border-radius:28px;padding:28px 13px 15px;position:relative;text-align:left;box-shadow:0 22px 45px #0d242022}
.notch{position:absolute;width:80px;height:14px;border-radius:0 0 10px 10px;background:#17201e;top:0;left:50%;transform:translateX(-50%)}
.phone small{font-weight:700;color:#1aa8aa}
.phone-image{height:185px;margin:15px 0;background:url('/untitled folder 2/Container (5).png') center/cover;border-radius:12px}
.bubble{background:#fff;padding:12px;border-radius:10px;font-size:12px;font-weight:700}
.chips{display:flex;gap:5px;margin-top:12px}
.chips span{background:${C.aqua};font-size:9px;padding:7px;border-radius:20px}
.phone-grid article>p{font-size:12px}

/* ===== TECHNOLOGY SECTION ===== */
.technology{background:#f1f4f1;padding:100px 0}
.tech-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:${C.line};border:1px solid ${C.line}}
.tech-grid article{background:#fff;padding:30px;min-height:220px}
.tech-grid article>span{color:#14a7a9;font-size:11px}
.tech-grid p{font-size:12px}
.graph{margin-top:35px;background:${C.aqua};padding:50px;display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:64px}
.graph-copy p{margin:20px 0}
.graph-visual{display:flex;justify-content:center;align-items:center}
.graph-visual img{max-width:100%;height:auto;border-radius:12px}
.deploy-grid,.timeline{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.deploy-grid article{border-top:2px solid ${C.teal};padding-top:18px}
.deploy-grid b,.timeline b{color:#18a8aa}
.deploy-grid p,.timeline p{font-size:12px}
.metrics{display:grid;grid-template-columns:repeat(4,1fr);background:${C.ink};color:#fff}
.metrics article{padding:38px;border-right:1px solid #45504d}
.metrics b,.metrics span{display:block}
.metrics b{font-family:'Playfair Display';font-size:45px;color:#76dedb}
.metrics span{font-size:12px}
.timeline article{border-left:1px solid ${C.line};padding:20px}
.cta-image{background:linear-gradient(90deg,#0b2d2be8,#0b2d2b44),url('/untitled folder 2/Group 7.png') center/cover;color:#fff;padding:100px 0}
.cta-image h2{max-width:750px;margin-bottom:32px}
.cta-image .actions a{color:#fff}
.final-cta{background:#f0f2ef;padding:64px;display:grid;grid-template-columns:1fr .5fr;gap:64px;align-items:center;margin-bottom:100px}
.final-cta h2{font-size:clamp(32px,4vw,48px)}
.final-cta p{font-size:13px;margin-top:16px}

/* ===== FOOTER ===== */
footer{background:#fff;color:${C.ink};padding:80px 0 30px;border-top:1px solid ${C.line}}
.footer-grid{display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:start}
.footer-brand{max-width:360px}
.footer-brand .logo-img{height:28px;width:auto}
.footer-brand p{margin-top:20px;font-size:14px;line-height:1.7;color:#5d6864}
.footer-links{display:flex;gap:80px}
.footer-col{display:flex;flex-direction:column;gap:14px}
.footer-col b{font-size:11px;font-weight:600;letter-spacing:.1em;color:#5d6864;margin-bottom:8px}
.footer-col a{font-size:14px;color:${C.ink};transition:color .2s}
.footer-col a:hover{color:#178b8d}
.copyright{border-top:1px solid ${C.line};margin-top:64px;padding-top:24px;display:flex;justify-content:flex-start;gap:40px;color:#8a9692;font-size:12px}
.copyright-location{color:#8a9692}
.modular-section{padding:100px 0;background:#fff}.modular-header{display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-bottom:60px;align-items:start}.modular-header-left h2{font-size:clamp(32px,4vw,48px);margin-top:20px}.modular-header-right p{font-size:17px;line-height:1.6;color:#5d6864}.modular-agents{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}.agent-module{background:#f8f8f6;border-radius:16px;padding:28px;border:1px solid ${C.line}}.agent-module-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}.agent-module-label{font-size:10px;font-weight:700;letter-spacing:.1em;color:#178b8d}.agent-module-tag{font-size:10px;font-weight:500;padding:6px 12px;border:1px solid ${C.line};border-radius:99px;color:#666}.agent-module-icon{font-size:28px;margin-bottom:12px}.agent-module h4{font-size:17px;margin:0 0 10px;font-weight:600}.agent-module p{font-size:14px;line-height:1.5;color:#5d6864;margin:0}
.network-section{padding:100px 0;background:#fff}.network-section h2{font-size:clamp(32px,4vw,52px);margin:20px 0 40px;max-width:700px}.network-flow{display:flex;align-items:center;justify-content:center;gap:16px;background:${C.ink};color:#fff;padding:20px 40px;border-radius:99px;font-size:14px;font-weight:500;margin-bottom:40px}.network-flow .flow-arrow{color:${C.teal}}.network-desc{font-size:15px;line-height:1.7;color:#5d6864;max-width:900px}
.team-section{padding:100px 0;background:#f8f8f6}.team-grid{display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:start}.team-left h2{font-size:clamp(28px,3.5vw,44px);margin:20px 0 24px;line-height:1.15}.team-left>p{font-size:15px;line-height:1.7;color:#5d6864;margin-bottom:32px}.team-tags{display:flex;flex-wrap:wrap;gap:10px}.team-tags span{padding:10px 18px;border:1px solid ${C.ink};border-radius:4px;font-size:11px;font-weight:600;letter-spacing:.05em}.team-cards{display:grid;grid-template-columns:1fr 1fr;gap:16px}.team-card{background:#fff;border-radius:12px;padding:28px;border:1px solid ${C.line}}.team-card h4{font-size:16px;font-weight:600;margin:0 0 12px}.team-card p{font-size:13px;line-height:1.5;color:#5d6864;margin:0}
.faq-section{padding:100px 0;background:#fff}.faq-grid{display:grid;grid-template-columns:1fr 1.5fr;gap:80px;align-items:start}.faq-left h2{font-size:clamp(48px,5vw,72px);margin:20px 0 24px;line-height:1}.faq-left>p{font-size:15px;line-height:1.7;color:#5d6864}.faq-right{display:flex;flex-direction:column}.faq-item{border-bottom:1px solid ${C.line};cursor:pointer}.faq-question{display:flex;justify-content:space-between;align-items:center;padding:24px 0;gap:20px}.faq-question span:first-child{font-size:16px;font-weight:500;color:${C.ink}}.faq-toggle{font-size:20px;color:#999;flex-shrink:0}.faq-answer{padding:0 0 24px;font-size:15px;line-height:1.7;color:#5d6864}
.enterprise-section{padding:100px 0;background:#fff}.enterprise-grid{display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:start}.enterprise-left h2{font-size:clamp(28px,3.5vw,44px);margin:20px 0 24px;line-height:1.15}.enterprise-left>p{font-size:15px;line-height:1.7;color:#5d6864;margin-bottom:32px}.enterprise-features{display:grid;grid-template-columns:1fr 1fr;gap:16px}.enterprise-feature{background:#f8f8f6;border-radius:8px;padding:20px 24px;border:1px solid ${C.line}}.enterprise-feature span{font-size:14px;font-weight:500;color:${C.ink}}
.shift-section{padding:80px 0;background:#f5f5f3;border-radius:24px;margin:0 24px 60px}.shift-grid{display:grid;grid-template-columns:1fr 1fr;gap:100px;align-items:start}.shift-left h2{font-size:clamp(32px,4vw,48px);margin:24px 0 0;line-height:1.1;color:${C.ink};font-family:'Playfair Display',serif;font-weight:500}.shift-eyebrow{font-size:11px;font-weight:700;letter-spacing:.15em;color:#178b8d;text-transform:uppercase}.shift-right{padding-top:0}.btn-dark{background:${C.ink};color:#fff;margin-bottom:32px;padding:14px 24px;font-size:13px;border-radius:99px;display:inline-flex;align-items:center;gap:8px}.btn-dark:hover{background:#2a3b3a}.shift-links{display:flex;flex-direction:column;gap:0;margin-bottom:24px}.shift-links a{display:flex;justify-content:space-between;align-items:center;padding:18px 0;border-bottom:1px solid rgba(0,0,0,.1);color:${C.ink};font-size:15px;font-weight:500;transition:color .2s}.shift-links a:first-child{border-top:1px solid rgba(0,0,0,.1)}.shift-links a:hover{color:#178b8d}.shift-arrow{color:${C.ink};font-size:14px}.shift-tags{font-size:13px;color:#888;line-height:1.8}
.intent-section{position:relative;padding:100px 0;overflow:hidden}.intent-bg{position:absolute;inset:0;background-size:cover;background-position:center}.intent-content{position:relative;z-index:1;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}.intent-left h2{font-size:clamp(32px,4vw,48px);margin:20px 0;color:${C.ink}}.intent-left>p{font-size:16px;line-height:1.6;color:#5d6864;margin-bottom:30px}.intent-left .eyebrow{color:${C.ink}}.intent-left .eyebrow span{background:${C.ink}}.intent-stats{display:grid;grid-template-columns:1fr 1fr;gap:16px}.intent-stat{background:rgba(255,255,255,.9);backdrop-filter:blur(10px);border-radius:16px;padding:24px}.intent-stat b{font-size:clamp(28px,3vw,40px);font-family:'Playfair Display',serif;display:block;margin-bottom:8px}.intent-stat h4{font-size:15px;margin:0 0 8px;font-weight:600}.intent-stat p{font-size:13px;line-height:1.5;color:#5d6864;margin:0}
.guardrails-section{padding:100px 0;background:#fff}.guardrails-header{display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-bottom:60px;align-items:start}.guardrails-header-left h2{font-size:clamp(32px,4vw,48px);margin-top:20px}.guardrails-header-right p{font-size:17px;line-height:1.6;color:#5d6864;margin-bottom:24px}.guardrails-grid{display:grid;grid-template-columns:1.2fr 1fr;gap:40px;margin-bottom:40px}.guardrails-cards{display:grid;grid-template-columns:1fr 1fr;gap:20px}.guardrail-card{background:#f8f8f6;border-radius:16px;padding:28px}.guardrail-label{font-size:11px;font-weight:700;letter-spacing:.1em;color:#178b8d;display:block;margin-bottom:12px}.guardrail-card h4{font-size:17px;margin:0 0 10px;font-weight:600}.guardrail-card p{font-size:14px;line-height:1.5;color:#5d6864;margin:0 0 16px}.guardrail-tag{font-size:11px;font-weight:500;color:#666;padding:10px 14px;background:#fff;border-radius:8px;display:inline-block}.guardrails-visual{position:relative;border-radius:24px;overflow:hidden;min-height:450px}.guardrails-visual-bg{position:absolute;inset:0;background-size:cover;background-position:center}.control-stack{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(255,255,255,.95);backdrop-filter:blur(20px);border-radius:20px;padding:28px;color:#1a2e2d;width:340px;font-size:13px;box-shadow:0 8px 40px rgba(0,0,0,.12)}.control-stack-header{font-size:15px;font-weight:600;color:#178b8d;margin-bottom:20px;text-align:center}.control-stack-content{background:#f5f7f7;border-radius:12px;padding:18px 20px;margin-bottom:20px;font-family:'SF Mono',Monaco,'Courier New',monospace;font-size:13px;line-height:1.7}.control-row{margin-bottom:6px}.control-row:last-child{margin-bottom:0}.control-key{color:#178b8d;font-weight:500}.control-success{color:#1a2e2d}.control-checks{display:flex;flex-direction:column;gap:14px;margin-bottom:24px;padding:0 4px}.control-checks div{display:flex;align-items:center;font-size:15px;font-weight:500;color:#1a2e2d}.check-dot{width:12px;height:12px;border-radius:50%;display:inline-block;margin-right:12px;flex-shrink:0}.check-dot.green{background:#22c55e}.control-footer{text-align:center}.delivery-badge{background:#3d4f4f;color:#fff;padding:12px 24px;border-radius:99px;font-size:13px;font-weight:500;display:inline-flex;align-items:center;gap:10px}.delivery-badge::before{content:'';width:8px;height:8px;background:#9ca3af;border-radius:50%}.guardrails-note{font-size:14px;color:#888;margin-top:20px}
.observability-section{padding:100px 0;background:#f8f8f6}.observability-header{display:grid;grid-template-columns:1.2fr 1fr;gap:60px;margin-bottom:60px;align-items:start}.observability-header-left h2{font-size:clamp(32px,4vw,48px);margin-top:20px}.observability-header-right p{font-size:17px;line-height:1.6;color:#5d6864}.observability-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:50px;align-items:center;margin-bottom:50px}.observability-steps{display:flex;flex-direction:column;gap:16px}.obs-step{background:#fff;border-radius:12px;padding:24px;display:flex;gap:20px;align-items:flex-start;border-left:3px solid ${C.teal}}.obs-step span{font-size:12px;color:#999;font-weight:500}.obs-step h4{font-size:16px;margin:0 0 6px;font-weight:600}.obs-step p{font-size:14px;line-height:1.5;color:#5d6864;margin:0}.observability-diagram{text-align:center}.observability-diagram img{max-width:100%;height:auto}.observability-features{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;background:#fff;border-radius:16px;padding:32px}.observability-features article{padding:0 20px;border-right:1px solid ${C.line}}.observability-features article:last-child{border-right:0}.observability-features h4{font-size:16px;margin:0 0 10px;font-weight:600}.observability-features p{font-size:13px;line-height:1.5;color:#5d6864;margin:0}
.omnipresence-section{padding:100px 0;background:#f8f8f6}.omnipresence-grid{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}.omnipresence-left h2{font-size:clamp(32px,4vw,48px);margin:20px 0}.omnipresence-left>p{font-size:17px;line-height:1.6;color:#5d6864;margin-bottom:30px}.omnipresence-tags{display:flex;flex-wrap:wrap;gap:10px}.omnipresence-tags span{padding:10px 18px;border:1px solid ${C.line};border-radius:99px;font-size:12px;font-weight:500;background:#fff}.omnipresence-right{text-align:center}.omnipresence-right img{max-width:100%;height:auto}.omnipresence-flow{display:flex;align-items:center;justify-content:center;gap:12px;margin-top:24px;background:${C.ink};color:#fff;padding:14px 28px;border-radius:99px;font-size:13px;font-weight:500}.flow-arrow{color:${C.teal}}
.orchestration-section{position:relative;padding:120px 0;overflow:hidden}.orchestration-bg{position:absolute;inset:0;background-size:cover;background-position:center;opacity:.7}.orchestration-content{position:relative;z-index:1}.orchestration-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-bottom:40px}.orchestration-card-main{background:rgba(255,255,255,.92);backdrop-filter:blur(10px);border-radius:24px;padding:40px}.orchestration-card-main h2{font-size:clamp(28px,3.5vw,42px);margin:20px 0}.orchestration-card-main>p{font-size:15px;line-height:1.6;color:#5d6864;margin-bottom:24px}.orchestration-tags{display:flex;gap:12px;flex-wrap:wrap}.orchestration-tags span{padding:8px 16px;border:1px solid ${C.line};border-radius:99px;font-size:11px;font-weight:600;letter-spacing:.05em}.orchestration-right{display:flex;flex-direction:column;gap:16px}.agent-card{background:rgba(255,255,255,.85);backdrop-filter:blur(10px);border-radius:16px;padding:20px 24px;display:flex;align-items:flex-start;gap:16px}.agent-num{font-size:12px;color:#999;font-weight:500}.agent-content{flex:1}.agent-content h4{font-size:16px;margin:0 0 6px;font-weight:600}.agent-content p{font-size:13px;line-height:1.5;color:#5d6864;margin:0}.agent-badge{background:rgba(53,196,199,.15);color:#178b8d;padding:6px 12px;border-radius:99px;font-size:11px;font-weight:700}.orchestration-features{display:grid;grid-template-columns:repeat(4,1fr);gap:20px;background:rgba(255,255,255,.9);backdrop-filter:blur(10px);border-radius:20px;padding:32px}.orchestration-features article{padding:0 20px;border-right:1px solid ${C.line}}.orchestration-features article:last-child{border-right:0}.orchestration-features span{font-size:12px;color:#999;font-weight:500}.orchestration-features h4{font-size:16px;margin:8px 0;font-weight:600}.orchestration-features p{font-size:13px;line-height:1.5;color:#5d6864;margin:0}
.market-shift{padding:100px 0;background:#fff}.market-shift-header{display:grid;grid-template-columns:1.2fr 1fr;gap:60px;margin-bottom:60px;align-items:start}.market-shift-left h2{font-size:clamp(32px,4vw,48px)}.market-shift-right p{font-size:17px;line-height:1.6}.market-shift-cards{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}.market-shift-card{background:#f8f8f6;border-radius:16px;padding:28px}.ms-icon{width:40px;height:40px;margin-bottom:20px;color:${C.ink}}.market-shift-card h3{font-size:18px;margin:0 0 10px}.market-shift-card>p{font-size:14px;line-height:1.5;color:#5d6864;margin-bottom:20px}.ms-checks{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px}.ms-checks li{display:flex;align-items:center;gap:8px;font-size:13px;color:#5d6864}.ms-checks svg{color:#178b8d;flex-shrink:0}
.how-epic-works{padding:100px 0;background:#f8f8f6}.how-epic-header{display:grid;grid-template-columns:1.2fr 1fr;gap:60px;margin-bottom:60px;align-items:start}.how-epic-header-left h2{font-size:clamp(32px,4vw,48px)}.how-epic-header-right p{font-size:17px;line-height:1.6}.how-epic-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}.how-epic-card{background:#fff;border-radius:20px;padding:30px;box-shadow:0 2px 20px rgba(0,0,0,.06)}.how-epic-num{width:32px;height:32px;border-radius:50%;background:${C.ink};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;margin-bottom:16px}.how-epic-card h3{font-size:20px;margin:0 0 12px}.how-epic-card>p{font-size:14px;line-height:1.6;color:#5d6864;margin-bottom:20px}.how-epic-visual{border-radius:16px;overflow:hidden;margin-bottom:16px}.how-epic-visual img{width:100%;height:auto;display:block}.how-epic-tag{font-size:11px;font-weight:700;letter-spacing:.1em;color:#178b8d}.tourism-cta{background:#fff;padding:60px 0;}.tourism-cta-inner{display:flex;align-items:center;justify-content:space-between;gap:40px;background:#f8f8f6;padding:40px;border-radius:24px;}.tourism-cta h2{color:${C.ink};font-size:clamp(28px,3.5vw,42px);max-width:500px}.tourism-cta .btn{background:${C.ink};color:#fff}
/* ===== RESPONSIVE: TABLET (1024px) ===== */
@media(max-width:1024px){
.wrap{padding:0 20px}
header{padding:0 20px}
.hero{grid-template-columns:1fr;gap:40px;padding:60px 0}
.hero-copy{order:-1}
.hero-visual{order:1}
.hero-visual img{max-width:100%;height:auto}
.actions{flex-direction:row;flex-wrap:wrap;gap:12px}
.actions .btn,.actions .btn-outline{flex:1;min-width:200px;justify-content:center}
.section-title{display:block;text-align:left}
.section-title>div{width:100%;margin-bottom:20px}
.section-title>p{width:100%;max-width:100%}
.comparison-cards{grid-template-columns:1fr}
.capabilities-grid{grid-template-columns:1fr 1fr}
.usecases-grid{grid-template-columns:1fr}
.banner-new{min-height:auto}
.banner-content{padding:50px 0}
.banner-left{max-width:100%}
.stats-row{flex-direction:column;gap:16px}
.destinations-header{grid-template-columns:1fr;gap:20px}
.destinations-header-left,.destinations-header-right{width:100%}
.destination-layout{grid-template-columns:1fr}
.destination-info-card{position:relative;bottom:auto;right:auto;max-width:100%;margin-top:20px}
.how-epic-header{grid-template-columns:1fr;gap:20px}
.how-epic-cards{grid-template-columns:1fr}
.market-shift-header{grid-template-columns:1fr;gap:20px}
.market-shift-cards{grid-template-columns:1fr 1fr}
.orchestration-grid{grid-template-columns:1fr}
.orchestration-features{grid-template-columns:1fr 1fr}
.orchestration-features article{border-right:0;border-bottom:1px solid ${C.line};padding-bottom:16px}
.orchestration-features article:last-child{border-bottom:0}
.omnipresence-grid{grid-template-columns:1fr;gap:30px}
.observability-header{grid-template-columns:1fr;gap:20px}
.observability-grid{grid-template-columns:1fr}
.observability-features{grid-template-columns:1fr 1fr}
.observability-features article{border-right:0;border-bottom:1px solid ${C.line};padding-bottom:16px}
.observability-features article:last-child{border-bottom:0}
.guardrails-header{grid-template-columns:1fr;gap:20px}
.guardrails-grid{grid-template-columns:1fr}
.guardrails-cards{grid-template-columns:1fr 1fr}
.guardrails-visual{min-height:280px;margin-top:24px}
.modular-header{grid-template-columns:1fr;gap:20px}
.modular-agents{grid-template-columns:1fr 1fr}
.intent-content{grid-template-columns:1fr;gap:30px}
.intent-stats{grid-template-columns:1fr 1fr}
.network-flow{flex-wrap:wrap;padding:16px 20px;border-radius:16px;gap:8px;font-size:13px}
.team-grid{grid-template-columns:1fr;gap:30px}
.team-cards{grid-template-columns:1fr 1fr}
.faq-grid{grid-template-columns:1fr;gap:30px}
.enterprise-grid{grid-template-columns:1fr;gap:30px}
.enterprise-features{grid-template-columns:1fr 1fr}
.shift-grid{grid-template-columns:1fr;gap:30px}
.shift-section{margin:0 16px 40px;padding:50px 0}
.tourism-cta-inner{flex-direction:column;text-align:center;gap:20px}
.footer-grid{grid-template-columns:1fr;gap:30px}
.footer-links{flex-direction:row;gap:40px}
.copyright{flex-direction:column;gap:6px;text-align:center}
}

/* ===== RESPONSIVE: MOBILE (768px) ===== */
@media(max-width:768px){
/* === GLOBAL MOBILE RESET === */
*{box-sizing:border-box}
html,body{overflow-x:hidden;width:100%}
.site{overflow-x:hidden}
.wrap{width:100%;max-width:100%;padding:0 16px;margin:0 auto;overflow-x:hidden}

/* === HEADER & MOBILE MENU === */
header{height:64px;padding:0;max-width:100%}
.header-inner{padding:0 16px;justify-content:space-between}
.logo{display:flex;align-items:center;margin-left:0}
.logo-img{height:28px;width:auto}
nav,.header-actions{display:none}
.menu{display:flex;align-items:center;justify-content:center;width:44px;height:44px;background:none;border:0;font-size:24px;cursor:pointer;padding:0;margin-left:auto}
nav.open{display:flex;position:fixed;left:0;right:0;top:64px;bottom:0;background:#fff;flex-direction:column;padding:24px 20px;z-index:1000;overflow-y:auto}
nav.open a{display:flex;align-items:center;min-height:48px;padding:12px 0;border-bottom:1px solid ${C.line};font-size:16px;font-weight:500}
nav.open a:last-child{border-bottom:0}
.mobile-menu-actions{display:flex;flex-direction:column;gap:12px;margin-top:24px;padding-top:24px;border-top:1px solid ${C.line}}
.mobile-menu-actions .btn{width:100%;min-height:48px;justify-content:center}
.mobile-menu-actions .login-link{display:flex;align-items:center;justify-content:center;min-height:48px;font-size:16px;font-weight:500}

/* === TYPOGRAPHY === */
h1{font-size:32px;line-height:1.1}
h2{font-size:26px;line-height:1.15}
h3{font-size:20px;line-height:1.2}
p{font-size:16px;line-height:1.6}
.eyebrow{font-size:10px;margin-bottom:16px;gap:8px}
.eyebrow span{width:20px}

/* === BUTTONS - Min 44px touch target === */
.btn{min-height:48px;padding:14px 20px;font-size:14px;gap:10px;border-radius:24px;width:100%;white-space:normal;word-wrap:break-word;text-align:center;line-height:1.3}
.btn-outline{min-height:48px;padding:14px 20px;font-size:14px;white-space:normal;word-wrap:break-word;text-align:center;line-height:1.3;border-radius:24px}
.btn-dark{min-height:48px;padding:14px 20px;font-size:14px;white-space:normal;word-wrap:break-word;text-align:center;line-height:1.3;border-radius:99px;background:${C.ink};color:#fff}

/* === HERO SECTION === */
.hero{display:flex;flex-direction:column;gap:32px;padding:40px 0}
.hero-copy{order:-1}
.hero-copy h1{font-size:32px;margin-bottom:16px}
.hero-copy>p{font-size:16px;margin:16px 0 24px;max-width:100%}
.hero-visual{order:1;width:100%}
.hero-visual img{width:100%;height:auto;max-width:100%;border-radius:12px;object-fit:contain}
.actions{display:flex;flex-direction:column;gap:12px;width:100%}
.actions .btn,.actions .btn-outline{width:100%;min-height:48px;justify-content:center}

/* === TRUST/FEATURE ITEMS === */
.trust{display:flex;flex-direction:column;gap:0;padding:24px 0;margin-top:32px}
.trust div{display:grid;grid-template-columns:32px 1fr;grid-template-rows:auto auto;gap:4px 12px;padding:20px 0;border-right:0;border-bottom:1px solid ${C.line};align-items:start}
.trust div:first-child{padding-top:0}
.trust div:last-child{border-bottom:0;padding-bottom:0}
.trust svg{flex-shrink:0;width:24px;height:24px;grid-row:1/3;align-self:start;margin-top:2px}
.trust b{font-size:15px;margin-bottom:0;grid-column:2;grid-row:1}
.trust span{font-size:14px;grid-column:2;grid-row:2}

/* === SECTION SPACING === */
.intro,.platform,.usecases,.mobile,.deployment,.credentials,.how,.faq{padding:48px 0}
.section-title{display:flex;flex-direction:column;gap:16px;margin-bottom:32px;text-align:left}
.section-title>div{width:100%}
.section-title h2{font-family: serif; font-size: 3.5rem; font-weight: 500; line-height: 1.2; letter-spacing: -0.02em;}
.section-title p{font-size:16px;max-width:100%}

/* === COMPARISON CARDS === */
.comparison-cards{display:flex;flex-direction:column;gap:20px;margin-top:32px}
.comparison-card{width:100%;padding:24px;border-radius:16px;min-height:auto}
.comparison-card h3{font-size:22px;margin-bottom:16px}
.card-label{font-size:10px;margin-bottom:16px}
.chat-demo{padding:16px 0;font-size:14px}
.user-msg{font-size:14px;padding:12px 16px;max-width:100%}
.ai-msg{font-size:14px;padding:12px 16px}
.feature-tags{display:flex;flex-wrap:wrap;gap:8px;margin:20px 0}
.feature-tags span{font-size:11px;padding:8px 12px}
.card-footer{font-size:11px;padding:14px 16px;min-height:48px}

/* === CAPABILITIES/SOLUTION CARDS === */
.capabilities{padding:48px 0}
.capabilities-grid{display:flex;flex-direction:column;gap:16px}
.capabilities-grid article{padding:24px;min-height:auto;height:auto}
.capabilities-grid h4{font-size:17px}
.capabilities-grid p{font-size:15px}
.cap-tags{font-size:11px;margin-top:16px}

/* === AUDIENCE/USE-CASE CARDS === */
.usecases{padding:48px 0}
.usecases-header{margin-bottom:32px;text-align:left;width:100%}
.usecases-header h2{font-size:26px;text-align:left;margin:0 0 12px}
.usecases-subtitle{font-size:15px}
.usecases-grid{display:flex;flex-direction:column;gap:20px}
.usecase-card{padding:24px;min-height:auto;height:auto;border-radius:16px}
.usecase-card:nth-child(1){background:#e8f5f4;border-color:#d0e8e6}
.usecase-card:nth-child(2){background:#f5f5f3;border-color:#e8e8e6}
.usecase-card:nth-child(3){background:#fff;border-color:#e8e8e6}
.usecase-label{font-size:10px;margin-bottom:16px}
.usecase-card h3{font-size:22px;margin-bottom:16px}
.usecase-card>p{font-size:15px;margin-bottom:20px}
.usecase-checks{margin-bottom:20px}
.usecase-checks li{font-size:14px;margin-bottom:12px;min-height:24px}
.usecase-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:20px}
.usecase-tags span{font-size:11px;padding:10px 14px;background:#fff}
.usecase-card .btn{width:100%;min-height:48px;margin-top:auto;background:${C.ink};color:#fff}

/* === BANNER SECTION === */
.banner-new{min-height:auto;padding:0;overflow:hidden}
.banner-bg{position:relative;width:100%;height:220px;background-position:center;background-size:cover;border-radius:0}
.banner-content{position:relative;background:#fff;padding:32px 0}
.banner-left{max-width:100%;padding:0}
.banner-left .eyebrow{margin-bottom:12px}
.banner-left h2{font-size:26px;margin-bottom:16px}
.banner-left>p{font-size:16px;margin-bottom:24px}
.stats-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:24px}
.stat-box{padding:16px;background:#f8f8f6;border-radius:12px;text-align:center}
.stat-box b{font-size:24px;margin-bottom:8px}
.stat-box span{font-size:13px}
.stat-box small{font-size:11px;margin-top:8px}
.banner-left .btn{width:100%;min-height:48px;white-space:normal;word-wrap:break-word;text-align:center;line-height:1.3;padding:16px 20px;border-radius:24px}

/* === DESTINATIONS SECTION === */
.destinations-section{padding:48px 0}
.destinations-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.destinations-header-left h2{font-size:26px}
.destinations-header-right{padding-top:0}
.destinations-header-right p{font-size:15px}
.destination-layout{display:flex;flex-direction:column;gap:24px}
.destination-image{width:100%;border-radius:12px;overflow:hidden}
.destination-image img{width:100%;height:auto;display:block}
.destination-info-card{position:relative;width:100%;padding:24px;margin-top:0;max-width:100%}
.destination-info-card h3{font-size:22px}
.destination-info-card>p{font-size:15px}
.dest-features{display:flex;flex-direction:column;gap:10px;margin-bottom:24px}
.dest-features span{font-size:12px;padding:12px 16px}

/* === HOW EPIC WORKS === */
.how-epic-works{padding:48px 0}
.how-epic-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.how-epic-header-left h2{font-size:26px}
.how-epic-header-right p{font-size:15px}
.how-epic-cards{display:flex;flex-direction:column;gap:20px}
.how-epic-card{padding:24px;min-height:auto}
.how-epic-num{width:32px;height:32px;font-size:13px}
.how-epic-card h3{font-size:18px}
.how-epic-card>p{font-size:15px}
.how-epic-visual img{width:100%;height:auto}
.how-epic-tag{font-size:11px}

/* === TOURISM CTA === */
.tourism-cta{padding:32px 0}
.tourism-cta-inner{display:flex;flex-direction:column;gap:20px;text-align:center}
.tourism-cta h2{font-size:24px;max-width:100%}
.tourism-cta .btn{width:100%;min-height:48px}

/* === MARKET SHIFT CARDS === */
.market-shift{padding:48px 0}
.market-shift-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.market-shift-left h2{font-size:26px}
.market-shift-right p{font-size:15px}
.market-shift-cards{display:flex;flex-direction:column;gap:16px}
.market-shift-card{padding:24px;min-height:auto}
.market-shift-card h3{font-size:18px}
.market-shift-card>p{font-size:15px}
.ms-checks li{font-size:14px;min-height:24px}

/* === ORCHESTRATION SECTION === */
.orchestration-section{padding:48px 0;position:relative;overflow:hidden}
.orchestration-bg{position:relative;width:100%;height:200px;border-radius:12px;margin-bottom:24px;background-size:cover;background-position:center}
.orchestration-content{position:relative;z-index:1}
.orchestration-grid{display:flex;flex-direction:column;gap:24px;margin-bottom:32px}
.orchestration-card-main{padding:24px;border-radius:16px;background:rgba(255,255,255,0.95)}
.orchestration-card-main h2{font-size:24px}
.orchestration-card-main>p{font-size:15px}
.orchestration-tags{display:flex;flex-wrap:wrap;gap:8px}
.orchestration-tags span{font-size:11px;padding:8px 14px}
.orchestration-right{display:flex;flex-direction:column;gap:12px}
.agent-card{padding:20px;gap:16px;flex-direction:column;align-items:flex-start;background:rgba(255,255,255,0.95)}
.agent-num{font-size:12px}
.agent-content h4{font-size:16px}
.agent-content p{font-size:14px}
.agent-badge{font-size:11px;padding:8px 12px;align-self:flex-start}
.orchestration-features{display:flex;flex-direction:column;gap:0;padding:24px;border-radius:16px;background:rgba(255,255,255,0.95)}
.orchestration-features article{padding:20px 0;border-right:0;border-bottom:1px solid ${C.line}}
.orchestration-features article:last-child{border-bottom:0;padding-bottom:0}
.orchestration-features article:first-child{padding-top:0}
.orchestration-features span{font-size:12px}
.orchestration-features h4{font-size:16px}
.orchestration-features p{font-size:14px}

/* === OMNIPRESENCE/OMNICHANNEL SECTION === */
.omnipresence-section{padding:48px 0}
.omnipresence-grid{display:flex;flex-direction:column;gap:32px}
.omnipresence-left h2{font-size:26px}
.omnipresence-left>p{font-size:15px;margin-bottom:20px}
.omnipresence-tags{display:flex;flex-wrap:wrap;gap:8px}
.omnipresence-tags span{padding:10px 16px;font-size:13px}
.omnipresence-right{width:100%}
.omnipresence-right img{width:100%;height:auto;max-width:280px;margin:0 auto;display:block}
.omnipresence-flow{flex-direction:column;padding:16px 20px;border-radius:12px;font-size:13px;gap:8px}
.flow-arrow{transform:rotate(90deg)}

/* === OBSERVABILITY SECTION === */
.observability-section{padding:48px 0}
.observability-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.observability-header-left h2{font-size:26px}
.observability-header-right p{font-size:15px}
.observability-grid{display:flex;flex-direction:column;gap:24px;margin-bottom:32px}
.observability-steps{display:flex;flex-direction:column;gap:12px}
.obs-step{padding:20px;gap:16px;flex-direction:column}
.obs-step span{font-size:12px}
.obs-step h4{font-size:16px}
.obs-step p{font-size:14px}
.observability-diagram{width:100%}
.observability-diagram img{width:100%;height:auto}
.observability-features{display:flex;flex-direction:column;gap:0;padding:24px;border-radius:16px}
.observability-features article{padding:20px 0;border-right:0;border-bottom:1px solid ${C.line}}
.observability-features article:last-child{border-bottom:0}
.observability-features h4{font-size:15px}
.observability-features p{font-size:14px}

/* === GUARDRAILS SECTION === */
.guardrails-section{padding:48px 0}
.guardrails-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.guardrails-header-left h2{font-size:26px}
.guardrails-header-right p{font-size:15px}
.guardrails-grid{display:flex;flex-direction:column;gap:24px;margin-bottom:24px}
.guardrails-cards{display:flex;flex-direction:column;gap:16px}
.guardrail-card{padding:24px;min-height:auto}
.guardrail-card h4{font-size:17px}
.guardrail-card p{font-size:15px}
.guardrail-tag{font-size:12px;padding:10px 14px}
.guardrails-visual{width:100%;min-height:auto;border-radius:16px;position:relative;overflow:hidden}
.guardrails-visual-bg{position:relative;width:100%;height:250px;border-radius:16px}
.guardrails-visual img{width:100%;height:auto}
.control-stack{position:relative;width:100%;max-width:100%;padding:24px;font-size:13px;margin-top:-40px;z-index:2;background:rgba(255,255,255,.98);backdrop-filter:blur(20px);border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,.1);transform:none;left:auto;top:auto}
.control-stack-header{font-size:14px;font-weight:600;color:#178b8d;text-align:center;margin-bottom:16px}
.control-stack-content{background:#f5f7f7;border-radius:10px;padding:14px 16px;margin-bottom:16px;font-family:'SF Mono',Monaco,'Courier New',monospace;font-size:12px;line-height:1.6}
.control-row{margin-bottom:4px}
.control-checks{gap:12px;margin-bottom:20px}
.control-checks div{font-size:14px}
.check-dot{width:10px;height:10px;margin-right:10px}
.delivery-badge{padding:10px 20px;font-size:12px}

/* === MODULAR AGENTS SECTION === */
.modular-section{padding:48px 0}
.modular-header{display:flex;flex-direction:column;gap:16px;margin-bottom:32px}
.modular-header-left h2{font-size:26px}
.modular-header-right p{font-size:15px}
.modular-agents{display:flex;flex-direction:column;gap:16px}
.agent-module{padding:24px;min-height:auto}
.agent-module-header{flex-wrap:wrap;gap:8px}
.agent-module-label{font-size:10px}
.agent-module-tag{font-size:11px;padding:6px 12px}
.agent-module-icon{font-size:28px}
.agent-module h4{font-size:17px}
.agent-module p{font-size:15px}

/* === INTENT/STATISTICS SECTION === */
.intent-section{padding:48px 0}
.intent-content{display:flex;flex-direction:column;gap:32px}
.intent-left h2{font-size:26px}
.intent-left>p{font-size:15px}
.intent-stats{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.intent-stat{padding:20px;text-align:center}
.intent-stat b{font-size:28px}
.intent-stat h4{font-size:14px}
.intent-stat p{font-size:13px}

/* === NETWORK/DATA FLOW SECTION === */
.network-section{padding:48px 0}
.network-section h2{font-size:26px}
.network-flow{display:flex;flex-direction:column;align-items:center;gap:8px;padding:20px;border-radius:16px;text-align:center;font-size:13px;width:100%;max-width:100%;word-wrap:break-word}
.network-flow span{display:block;text-align:center}
.network-flow .flow-arrow{transform:rotate(90deg);font-size:16px}
.network-desc{font-size:15px}

/* === TEAM SECTION === */
.team-section{padding:48px 0}
.team-grid{display:flex;flex-direction:column;gap:32px}
.team-left h2{font-size:26px}
.team-left>p{font-size:15px;margin-bottom:20px}
.team-tags{display:flex;flex-wrap:wrap;gap:8px}
.team-tags span{padding:10px 16px;font-size:12px;min-height:44px;display:flex;align-items:center}
.team-cards{display:flex;flex-direction:column;gap:16px}
.team-card{padding:24px;min-height:auto}
.team-card h4{font-size:16px}
.team-card p{font-size:14px}

/* === FAQ SECTION === */
.faq-section{padding:48px 0}
.faq-grid{display:flex;flex-direction:column;gap:24px}
.faq-left{margin-bottom:8px}
.faq-left h2{font-size:32px}
.faq-left>p{font-size:15px}
.faq-right{width:100%}
.faq-item{border-bottom:1px solid ${C.line}}
.faq-question{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:20px 0;min-height:56px;cursor:pointer}
.faq-question span:first-child{font-size:16px;font-weight:500;flex:1;line-height:1.4}
.faq-toggle{font-size:24px;width:24px;height:24px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.faq-answer{padding:0 0 20px;font-size:15px;line-height:1.6}

/* === ENTERPRISE SECTION === */
.enterprise-section{padding:48px 0}
.enterprise-grid{display:flex;flex-direction:column;gap:32px}
.enterprise-left h2{font-size:26px}
.enterprise-left>p{font-size:15px;margin-bottom:24px}
.enterprise-features{display:flex;flex-direction:column;gap:12px}
.enterprise-feature{padding:16px 20px;min-height:48px;display:flex;align-items:center}
.enterprise-feature span{font-size:15px}

/* === SHIFT/PARTNERSHIP SECTION === */
.shift-section{padding:40px 20px;margin:0 0 32px;border-radius:16px}
.shift-grid{display:flex;flex-direction:column;gap:32px}
.shift-left h2{font-size:26px;margin-top:16px}
.shift-eyebrow{font-size:10px}
.shift-right{padding-top:0}
.shift-links{margin-bottom:24px}
.shift-links a{font-size:15px;padding:16px 0;min-height:52px;white-space:normal;word-wrap:break-word}
.shift-tags{font-size:12px}
.btn-dark{width:100%;min-height:48px;white-space:normal;word-wrap:break-word;text-align:center;padding:16px 20px;line-height:1.3;background:${C.ink};color:#fff;border-radius:99px}

/* === FINAL CTA SECTION === */
.final-cta{display:flex;flex-direction:column;gap:24px;padding:32px 24px;margin-bottom:48px;text-align:center}
.final-cta h2{font-size:26px}
.final-cta p{font-size:15px;margin-top:12px}
.final-cta .actions{display:flex;flex-direction:column;gap:12px;width:100%}
.final-cta .btn{width:100%;min-height:48px}

/* === FOOTER === */
footer{padding:48px 0 24px}
.footer-grid{display:flex;flex-direction:column;gap:32px}
.footer-brand{max-width:100%}
.footer-brand .logo-img{height:28px}
.footer-brand p{font-size:14px;margin-top:16px}
.footer-links{display:flex;flex-direction:column;gap:32px}
.footer-col{display:flex;flex-direction:column;gap:16px}
.footer-col b{font-size:11px;margin-bottom:4px}
.footer-col a{font-size:15px;min-height:44px;display:flex;align-items:center}
.copyright{flex-direction:column;gap:12px;text-align:center;margin-top:32px;padding-top:24px;font-size:13px}

/* === CTA IMAGE/BARS === */
.cta-image{padding:48px 0}
.cta-image h2{font-size:24px;margin-bottom:24px}
.cta-image .actions{display:flex;flex-direction:column;gap:12px}
.cta-image .btn{width:100%;min-height:48px}

/* === PHONE GRID === */
.phone-grid{display:flex;flex-direction:column;gap:32px}
.phone{width:100%;max-width:260px;margin:0 auto}

/* === TECHNOLOGY SECTION === */
.technology{padding:48px 0}
.tech-grid{display:flex;flex-direction:column;gap:1px}
.tech-grid article{padding:24px;min-height:auto}

/* === METRICS === */
.metrics{display:grid;grid-template-columns:1fr 1fr}
.metrics article{padding:24px;border-right:0;border-bottom:1px solid #45504d}
.metrics article:nth-child(odd){border-right:1px solid #45504d}
.metrics article:nth-last-child(-n+2){border-bottom:0}
.metrics b{font-size:32px}
.metrics span{font-size:13px}

/* === GRAPH === */
.graph{display:flex;flex-direction:column;gap:32px;padding:32px 24px}
.graph-copy p{font-size:15px}
.graph-visual img{width:100%;height:auto}

/* === DEPLOY/TIMELINE GRIDS === */
.deploy-grid,.timeline{display:flex;flex-direction:column;gap:20px}
.deploy-grid article,.timeline article{padding:20px 0;border-left:0;border-top:2px solid ${C.teal}}
}

/* ===== RESPONSIVE: SMALL MOBILE (480px) ===== */
@media(max-width:480px){
.wrap{padding:0 16px}
header{padding:0 16px}
h1{font-size:28px}
h2{font-size:24px}
h3{font-size:18px}
.hero-copy h1{font-size:28px}
.hero{padding:32px 0;gap:24px}
.hero-copy>p{font-size:15px}
.trust div{padding:16px 0}
.comparison-card{padding:20px}
.comparison-card h3{font-size:20px}
.usecase-card{padding:20px}
.usecase-card h3{font-size:20px}
.banner-bg{height:180px}
.banner-content{padding:24px 0}
.banner-left h2{font-size:24px}
.stats-row{grid-template-columns:1fr}
.stat-box b{font-size:22px}
.destinations-header-left h2{font-size:24px}
.destination-info-card{padding:20px}
.destination-info-card h3{font-size:20px}
.how-epic-card{padding:20px}
.how-epic-card h3{font-size:17px}
.tourism-cta h2{font-size:22px}
.market-shift-card{padding:20px}
.market-shift-card h3{font-size:17px}
.orchestration-card-main{padding:20px}
.orchestration-card-main h2{font-size:22px}
.agent-card{padding:16px}
.orchestration-features{padding:20px}
.omnipresence-left h2{font-size:24px}
.omnipresence-right img{max-width:240px}
.observability-header-left h2{font-size:24px}
.obs-step{padding:16px}
.observability-features{padding:20px}
.guardrails-header-left h2{font-size:24px}
.guardrail-card{padding:20px}
.modular-header-left h2{font-size:24px}
.agent-module{padding:20px}
.intent-left h2{font-size:24px}
.intent-stats{grid-template-columns:1fr}
.intent-stat{padding:16px}
.intent-stat b{font-size:26px}
.network-section h2{font-size:24px}
.network-flow{padding:20px;font-size:13px}
.team-left h2{font-size:24px}
.team-card{padding:20px}
.faq-left h2{font-size:28px}
.faq-question span:first-child{font-size:15px}
.enterprise-left h2{font-size:24px}
.enterprise-feature{padding:14px 16px}
.shift-section{padding:32px 16px}
.shift-left h2{font-size:24px}
.shift-links a{font-size:14px;padding:14px 0}
.final-cta{padding:28px 20px}
.final-cta h2{font-size:24px}
footer{padding:40px 0 20px}
.footer-brand p{font-size:13px}
.footer-col a{font-size:14px}
.copyright{font-size:12px}
}

/* ===== RESPONSIVE: EXTRA SMALL (360px) ===== */
@media(max-width:360px){
.wrap{padding:0 12px}
header{padding:0 12px}
h1{font-size:26px}
h2{font-size:22px}
.hero-copy h1{font-size:26px}
.btn{padding:12px 16px;font-size:13px}
.comparison-card{padding:16px}
.usecase-card{padding:16px}
.stat-box{padding:14px}
.stat-box b{font-size:20px}
.destination-info-card{padding:16px}
.how-epic-card{padding:16px}
.market-shift-card{padding:16px}
.orchestration-card-main{padding:16px}
.orchestration-features{padding:16px}
.agent-card{padding:14px}
.obs-step{padding:14px}
.observability-features{padding:16px}
.guardrail-card{padding:16px}
.agent-module{padding:16px}
.intent-stat{padding:14px}
.network-flow{padding:16px}
.team-card{padding:16px}
.enterprise-feature{padding:12px 14px}
.shift-section{padding:24px 12px}
.final-cta{padding:24px 16px}
}
`;
